import React, { useState, useEffect } from 'react';
import { Table, Input, Switch, Tag, Typography, Card, Avatar, message, Alert } from 'antd';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const StyledTableCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  /* Fix pagination text colors for dark theme */
  .ant-pagination {
    color: ${COLORS.textPrimary};
  }
  
  .ant-pagination-total-text {
    color: ${COLORS.textSecondary} !important;
  }
  
  .ant-pagination-item {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    
    a {
      color: ${COLORS.textPrimary} !important;
    }
    
    &:hover {
      border-color: ${COLORS.accent} !important;
    }
  }
  
  .ant-pagination-item-active {
    background: ${COLORS.accent} !important;
    border-color: ${COLORS.accent} !important;
    
    a {
      color: #000 !important;
    }
  }
  
  .ant-pagination-prev, .ant-pagination-next {
    .ant-pagination-item-link {
      background: ${COLORS.bgPrimary} !important;
      border-color: ${COLORS.border} !important;
      color: ${COLORS.textPrimary} !important;
    }
    
    &:hover .ant-pagination-item-link {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
    }
  }
  
  .ant-pagination-disabled {
    .ant-pagination-item-link {
      color: ${COLORS.textSecondary} !important;
    }
  }
  
  .ant-pagination-options {
    .ant-select-selector {
      background: ${COLORS.bgPrimary} !important;
      border-color: ${COLORS.border} !important;
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-select-arrow {
      color: ${COLORS.textSecondary} !important;
    }
  }
  
  .ant-table {
    background: transparent !important;
    color: ${COLORS.textPrimary};
    
    @media (max-width: 768px) {
      min-width: 800px;
    }
  }
  
  .ant-table-thead > tr > th {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    
    @media (max-width: 768px) {
      padding: 8px 4px !important;
      font-size: 12px !important;
    }
  }
  
  /* Fixed column styling for dark theme */
  .ant-table-thead > tr > th.ant-table-cell-fix-left {
    background: ${COLORS.bgPrimary} !important;
  }
  
  .ant-table-tbody > tr > td.ant-table-cell-fix-left {
    background: ${COLORS.bgSecondary} !important;
  }
  
  .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
    background: rgba(255, 255, 255, 0.04) !important;
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${COLORS.border} !important;
    
    @media (max-width: 768px) {
      padding: 8px 4px !important;
      font-size: 12px !important;
    }
  }
  
  .ant-table-tbody > tr:hover > td {
    background: rgba(255, 255, 255, 0.02) !important;
  }
`;

const SupportedBlockchainNetworks: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [networks, setNetworks] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
  });

  useEffect(() => {
    fetchNetworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pagination.current, pagination.pageSize]);

  const fetchNetworks = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId: pagination.current,
        perPage: pagination.pageSize,
      };
      if (search) {
        params.search = search;
      }

      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_SUPPORTED_BLOCKCHAIN_NETWORKS, {
        params,
      });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setNetworks(items);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination(prev => ({
            ...prev,
            total: paginator.itemCount || paginator.totalCount || paginator.total || 0,
          }));
        }
      }
    } catch (error: any) {
      console.error('Error fetching supported blockchain networks:', error);
      message.error('Failed to load supported blockchain networks');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleToggleSupport = async (networkId: string, isSupported: boolean) => {
    try {
      const endpoint = API_ENDPOINTS.MERCHANT_TOGGLE_SUPPORTED_BLOCKCHAIN_NETWORK.replace('{blockchainNetworkId}', networkId);
      await apiClient.put(endpoint, {
        isSupported: !isSupported,
      });
      message.success(`Blockchain network ${!isSupported ? 'enabled' : 'disabled'}`);
      fetchNetworks();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update support status');
    }
  };

  const columns = [
    {
      title: 'Enabled',
      key: 'isSupported',
      width: 80,
      fixed: 'left' as const,
      render: (_: any, record: any) => (
        <Switch
          checked={record.isSupported || false}
          onChange={(checked) => handleToggleSupport(record.id, record.isSupported)}
        />
      ),
    },
    {
      title: 'Network',
      key: 'network',
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {record.iconUrl && (
            <Avatar src={record.iconUrl} size={32} />
          )}
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            {record.abbreviation && (
              <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                {record.abbreviation}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'blockchainType',
      key: 'blockchainType',
      render: (type: string, record: any) => {
        if (!type) return '-';

        let typeDisplay = type;
        let tooltip = '';

        if (type === 'BITCOIN') {
          typeDisplay = 'Bitcoin (Native SegWit)';
          tooltip = 'Supports both Bech32 (bc1q...) and legacy formats for maximum compatibility';
        }

        return (
          <div>
            <Tag color="cyan" title={tooltip}>
              {typeDisplay}
            </Tag>
            {type === 'BITCOIN' && (
              <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '2px' }}>
                Bech32 + Legacy
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Network ID',
      dataIndex: 'networkId',
      key: 'networkId',
      render: (id: number) => id ?? '-',
    },
    {
      title: 'Is Congested',
      dataIndex: 'isCongested',
      key: 'isCongested',
      render: (isCongested: boolean) => <Tag color={isCongested ? 'orange' : 'default'}>{isCongested ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'AsoRock Ack',
      dataIndex: 'asorockAcknowledged',
      key: 'asorockAcknowledged',
      render: (acknowledged: boolean) => <Tag color={acknowledged ? 'green' : 'red'}>{acknowledged ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Network Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Supported Blockchain Networks
        </Title>
      </HeaderContainer>

      <Alert
        message="Bitcoin Native SegWit Support"
        description="Bitcoin networks support both modern Bech32 (bc1q...) addresses for lower fees and legacy formats for maximum compatibility."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 24 }}
      />

      <FiltersContainer>
        <Input
          placeholder="Search networks..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 300 }}
        />
      </FiltersContainer>

      <StyledTableCard>
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={networks}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => <span style={{ color: COLORS.textSecondary }}>Total {total} networks</span>,
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </StyledTableCard>
    </PageContainer>
  );
};

export default SupportedBlockchainNetworks;

